import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useRef, useState } from "react";
import type { Todo } from "../types/todo";

const LONG_PRESS_DURATION = 300;

type ItemCountBadgeProps = {
  todos: Todo[];
  onDeleteCompleted: () => void;
};

export const ItemCountBadge = ({
  todos,
  onDeleteCompleted,
}: ItemCountBadgeProps) => {
  const [showDetail, setShowDetail] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPress = useRef(false);
  const usedTouch = useRef(false);

  const checkedCount = todos.filter((t) => t.checked).length;
  const uncheckedCount = todos.length - checkedCount;

  const handlePressStart = (source: "touch" | "mouse") => {
    if (source === "touch") usedTouch.current = true;
    if (source === "mouse" && usedTouch.current) return;

    isLongPress.current = false;
    timerRef.current = setTimeout(() => {
      isLongPress.current = true;
      if (checkedCount > 0) {
        setDialogOpen(true);
      }
    }, LONG_PRESS_DURATION);
  };

  const handlePressEnd = (source: "touch" | "mouse") => {
    if (source === "mouse" && usedTouch.current) {
      usedTouch.current = false;
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!isLongPress.current) {
      setShowDetail((prev) => !prev);
    }
  };

  const handleConfirmDelete = () => {
    setDialogOpen(false);
    onDeleteCompleted();
  };

  const displayText = showDetail
    ? `${uncheckedCount} 未完了 / ${checkedCount} 完了`
    : `${todos.length} items`;

  return (
    <>
      <span
        className="min-w-[60px] select-none whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-right text-sm text-muted-foreground"
        onTouchStart={() => handlePressStart("touch")}
        onTouchEnd={() => handlePressEnd("touch")}
        onMouseDown={() => handlePressStart("mouse")}
        onMouseUp={() => handlePressEnd("mouse")}
      >
        {displayText}
      </span>
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>完了済みアイテムを削除</AlertDialogTitle>
            <AlertDialogDescription>
              完了済み {checkedCount} 件を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
